import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const getApiBase = () => {
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : apiUrl + '/api';
};

const API_BASE = getApiBase();
const authHeader = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

const VehiculeManagement = () => {
    const [vehicules, setVehicules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState([]);
    const [view, setView] = useState('list'); // 'list', 'details', 'add'
    const [selectedVehicule, setSelectedVehicule] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [newNote, setNewNote] = useState('');
    
    // Form state for new vehicle
    const [formData, setFormData] = useState({
        matricule: '',
        marque: '',
        type: '',
        assurance: '',
        visiteTechnique: '',
        vignette: ''
    });

    const fetchVehicules = useCallback(async () => {
        setLoading(true);
        try {
            const [resVeh, resStats] = await Promise.all([
                axios.get(`${API_BASE}/vehicules`, authHeader()),
                axios.get(`${API_BASE}/vehicules/stats/global`, authHeader())
            ]);
            setVehicules(resVeh.data);
            setStats(resStats.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVehicules();
    }, [fetchVehicules]);

    const getStatusColor = (status) => {
        if (status === 'expire') return '#ef4444'; // Red
        if (status === 'expire_bientot') return '#f59e0b'; // Orange
        return '#10b981'; // Green
    };

    const getStatusLabel = (status) => {
        if (status === 'expire') return '🔴 Expiré';
        if (status === 'expire_bientot') return '🟠 Expire bientôt';
        return '🟢 Valide';
    };

    const handleUpdateDoc = async (vehicule, docType) => {
        const docLabel = docType === 'assurance' ? 'Assurance' : 
                         docType === 'vignette' ? 'Vignette' : 'Visite technique';
        
        const { value: formValues } = await Swal.fire({
            title: `Renouvellement ${docLabel}`,
            html:
                `<div style="text-align: left;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Nouvelle date d'expiration</label>` +
                `<input id="swal-input1" class="swal2-input" type="date" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Date du paiement</label>` +
                `<input id="swal-input-date" class="swal2-input" type="date" value="${new Date().toISOString().split('T')[0]}" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Montant du paiement (TND)</label>` +
                `<input id="swal-input2" class="swal2-input" type="number" step="0.01" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Mode de paiement</label>` +
                `<select id="swal-input3" class="swal2-input" style="margin-bottom: 15px; width: 85%;">` +
                    `<option value="especes">Espèces</option>` +
                    `<option value="cheque">Chèque</option>` +
                    `<option value="virement">Virement</option>` +
                    `<option value="versement_bancaire">Versement bancaire</option>` +
                    `<option value="carte">Carte</option>` +
                `</select>` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Nom du recepteur</label>` +
                `<input id="swal-input-recepteur" class="swal2-input" type="text" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Notes / Référence</label>` +
                `<input id="swal-input4" class="swal2-input" type="text" style="width: 85%;">` +
                `</div>`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Confirmer le paiement',
            cancelButtonText: 'Annuler',
            preConfirm: () => {
                return [
                    document.getElementById('swal-input1').value,
                    document.getElementById('swal-input-date').value,
                    document.getElementById('swal-input2').value,
                    document.getElementById('swal-input3').value,
                    document.getElementById('swal-input-recepteur').value,
                    document.getElementById('swal-input4').value
                ]
            }
        });

        if (formValues) {
            const [dateExp, datePaiement, montant, mode, nomRecepteur, notes] = formValues;
            if (!dateExp || !montant || !datePaiement) {
                Swal.fire('Erreur', 'La date d\'expiration, la date de paiement et le montant sont obligatoires', 'error');
                return;
            }
            if (mode === 'especes' && !nomRecepteur) {
                Swal.fire('Erreur', 'Nom du recepteur obligatoire pour un paiement en espèces', 'error');
                return;
            }

            try {
                await axios.post(`${API_BASE}/vehicules/${vehicule._id}/expenses`, {
                    type: docLabel,
                    categorie: 'Assurance / Vignette',
                    montant: parseFloat(montant),
                    datePaiement: datePaiement,
                    modePaiement: mode,
                    nomRecepteur: mode === 'especes' ? nomRecepteur : undefined,
                    notes: notes,
                    updateExpirationDate: dateExp
                }, authHeader());

                Swal.fire('Succès', `${docLabel} mise à jour et dépense enregistrée.`, 'success');
                fetchVehicules();
            } catch (err) {
                Swal.fire('Erreur', err.response?.data?.message || 'Une erreur est survenue', 'error');
            }
        }
    };

    const handleAddOtherExpense = async (vehicule) => {
        const { value: formValues } = await Swal.fire({
            title: `Nouvelle dépense - ${vehicule.matricule}`,
            html:
                `<div style="text-align: left;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Type de dépense</label>` +
                `<select id="swal-type" class="swal2-input" style="margin-bottom: 15px; width: 85%;">` +
                    `<option value="Vidange">Vidange</option>` +
                    `<option value="Réparation">Réparation</option>` +
                    `<option value="Maintenance générale">Maintenance générale</option>` +
                    `<option value="Pièces de rechange">Pièces de rechange</option>` +
                    `<option value="Carburant">Carburant</option>` +
                    `<option value="Autre">Autre</option>` +
                `</select>` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Date du paiement</label>` +
                `<input id="swal-date" class="swal2-input" type="date" value="${new Date().toISOString().split('T')[0]}" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Montant (TND)</label>` +
                `<input id="swal-montant" class="swal2-input" type="number" step="0.01" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Mode de paiement</label>` +
                `<select id="swal-mode" class="swal2-input" style="margin-bottom: 15px; width: 85%;">` +
                    `<option value="especes">Espèces</option>` +
                    `<option value="cheque">Chèque</option>` +
                    `<option value="virement">Virement</option>` +
                    `<option value="versement_bancaire">Versement bancaire</option>` +
                    `<option value="carte">Carte</option>` +
                `</select>` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Nom du recepteur</label>` +
                `<input id="swal-recepteur" class="swal2-input" type="text" style="margin-bottom: 15px; width: 85%;">` +
                `<label style="display: block; margin-bottom: 5px; font-weight: bold;">Description / Notes</label>` +
                `<input id="swal-notes" class="swal2-input" type="text" style="width: 85%;">` +
                `</div>`,
            showCancelButton: true,
            confirmButtonText: 'Enregistrer',
            cancelButtonText: 'Annuler',
            preConfirm: () => {
                const type = document.getElementById('swal-type').value;
                const date = document.getElementById('swal-date').value;
                const montant = document.getElementById('swal-montant').value;
                const mode = document.getElementById('swal-mode').value;
                const nomRecepteur = document.getElementById('swal-recepteur').value;
                const notes = document.getElementById('swal-notes').value;
                
                if (!montant || !date) {
                    Swal.showValidationMessage('Date et Montant sont obligatoires');
                    return false;
                }
                if (mode === 'especes' && !nomRecepteur) {
                    Swal.showValidationMessage('Nom du recepteur obligatoire pour un paiement en espèces');
                    return false;
                }
                
                return [type, date, montant, mode, nomRecepteur, notes];
            }
        });

        if (formValues) {
            const [type, date, montant, mode, nomRecepteur, notes] = formValues;
            const categorie = type === 'Carburant' ? 'Carburant' : 'Dépenses générales';

            try {
                await axios.post(`${API_BASE}/vehicules/${vehicule._id}/expenses`, {
                    type,
                    categorie,
                    montant: parseFloat(montant),
                    datePaiement: date,
                    modePaiement: mode,
                    nomRecepteur: mode === 'especes' ? nomRecepteur : undefined,
                    notes
                }, authHeader());
                Swal.fire('Succès', 'Dépense enregistrée', 'success');
                fetchVehicules();
            } catch (err) {
                Swal.fire('Erreur', 'Impossible d\'enregistrer la dépense', 'error');
            }
        }
    };

    const handleAddNote = async (vehiculeId) => {
        if (!newNote.trim()) return;
        try {
            const res = await axios.post(`${API_BASE}/vehicules/${vehiculeId}/notes`, { content: newNote }, authHeader());
            setSelectedVehicule(res.data);
            setNewNote('');
            Swal.fire('Succès', 'Note ajoutée', 'success');
            fetchVehicules();
        } catch (err) {
            Swal.fire('Erreur', 'Impossible d\'ajouter la note', 'error');
        }
    };

    const handleDeleteVehicule = async (vehicule) => {
        const result = await Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: `Le véhicule ${vehicule.matricule} sera définitivement supprimé.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${API_BASE}/vehicules/${vehicule._id}`, authHeader());
                Swal.fire('Supprimé !', 'Le véhicule a été supprimé.', 'success');
                fetchVehicules();
            } catch (err) {
                Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la suppression', 'error');
            }
        }
    };

    const handleEditVehicule = (vehicule) => {
        setFormData({
            matricule: vehicule.matricule,
            marque: vehicule.marque,
            type: vehicule.type,
            assurance: vehicule.assurance?.dateExpiration ? new Date(vehicule.assurance.dateExpiration).toISOString().split('T')[0] : '',
            visiteTechnique: vehicule.visiteTechnique?.dateExpiration ? new Date(vehicule.visiteTechnique.dateExpiration).toISOString().split('T')[0] : '',
            vignette: vehicule.vignette?.dateExpiration ? new Date(vehicule.vignette.dateExpiration).toISOString().split('T')[0] : '',
            kilometrage: vehicule.kilometrage || 0,
            actif: vehicule.actif
        });
        setSelectedVehicule(vehicule);
        setIsEditing(true);
        setView('add');
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!formData.matricule || !formData.marque || !formData.type) {
            Swal.fire('Erreur', 'Veuillez remplir les champs obligatoires (*)', 'error');
            return;
        }

        try {
            setLoading(true);
            if (isEditing && selectedVehicule) {
                await axios.put(`${API_BASE}/vehicules/${selectedVehicule._id}`, formData, authHeader());
                Swal.fire('Succès', 'Véhicule modifié avec succès', 'success');
            } else {
                await axios.post(`${API_BASE}/vehicules`, formData, authHeader());
                Swal.fire('Succès', 'Véhicule ajouté avec succès', 'success');
            }
            setFormData({ matricule: '', marque: '', type: '', assurance: '', visiteTechnique: '', vignette: '' });
            setIsEditing(false);
            setSelectedVehicule(null);
            setView('list');
            fetchVehicules();
        } catch (err) {
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de l\'opération', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showDetails = async (vehicule) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/vehicules/${vehicule._id}/expenses`, authHeader());
            setExpenses(res.data);
            setSelectedVehicule(vehicule);
            setView('details');
        } catch (err) {
            Swal.fire('Erreur', 'Impossible de charger l\'historique', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading && view === 'list') return <LoadingSpinner message="Chargement des véhicules..." />;

    return (
        <div className="veh-mgmt-container">
            {view === 'list' && (
                <>
                    <div className="veh-header-actions">
                        <h1>Gestion du Parc Automobile</h1>
                        <button className="btn-add-veh" onClick={() => {
                            setIsEditing(false);
                            setFormData({ matricule: '', marque: '', type: '', assurance: '', visiteTechnique: '', vignette: '' });
                            setView('add');
                        }}>
                            ➕ Ajouter un véhicule
                        </button>
                    </div>
                    <div className="veh-dashboard-grid">
                        {vehicules.map(v => {
                            const total = stats.find(s => s._id === v._id)?.total || 0;
                            return (
                                <div key={v._id} className="veh-card">
                                    <div className="veh-card-header">
                                        <div className="veh-plate">{v.matricule}</div>
                                        <div className="veh-brand">{v.marque} - {v.type}</div>
                                    </div>
                                    
                                    <div className="veh-docs-status">
                                        <div className="veh-doc-row" onClick={() => handleUpdateDoc(v, 'assurance')}>
                                            <span>Assurance</span>
                                            <span className="veh-status-badge" style={{ background: getStatusColor(v.statutsDocuments?.assurance) }}>
                                                {getStatusLabel(v.statutsDocuments?.assurance)}
                                            </span>
                                        </div>
                                        <div className="veh-doc-row" onClick={() => handleUpdateDoc(v, 'visiteTechnique')}>
                                            <span>Visite Tech.</span>
                                            <span className="veh-status-badge" style={{ background: getStatusColor(v.statutsDocuments?.visiteTechnique) }}>
                                                {getStatusLabel(v.statutsDocuments?.visiteTechnique)}
                                            </span>
                                        </div>
                                        <div className="veh-doc-row" onClick={() => handleUpdateDoc(v, 'vignette')}>
                                            <span>Vignette</span>
                                            <span className="veh-status-badge" style={{ background: getStatusColor(v.statutsDocuments?.vignette) }}>
                                                {getStatusLabel(v.statutsDocuments?.vignette)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="veh-card-footer">
                                        <div className="veh-total-cost">
                                            <span className="label">Total Dépenses :</span>
                                            <span className="value">{total.toLocaleString()} TND</span>
                                        </div>
                                        <div className="veh-actions">
                                            <button className="btn-icon" title="Modifier" onClick={() => handleEditVehicule(v)}>✏️</button>
                                            <button className="btn-icon" title="Historique" onClick={() => showDetails(v)}>📊</button>
                                            <button className="btn-icon" title="Ajouter dépense" onClick={() => handleAddOtherExpense(v)}>➕</button>
                                            <button className="btn-icon btn-delete" title="Supprimer" onClick={() => handleDeleteVehicule(v)}>🗑️</button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {view === 'add' && (
                <div className="veh-form-view">
                    <button className="btn-back" onClick={() => setView('list')}>← Retour au dashboard</button>
                    
                    <div className="veh-form-card">
                        <div className="veh-form-header">
                            <h2>{isEditing ? `Modifier le véhicule ${selectedVehicule.matricule}` : 'Ajouter un nouveau véhicule'}</h2>
                            <p>{isEditing ? 'Mettez à jour les informations du véhicule.' : 'Remplissez les informations techniques et administratives du véhicule.'}</p>
                        </div>

                        <form onSubmit={handleFormSubmit} className="veh-form-grid">
                            <div className="form-section">
                                <h3>Informations Générales</h3>
                                <div className="form-group">
                                    <label>Matricule *</label>
                                    <input name="matricule" value={formData.matricule} onChange={handleFormChange} placeholder="Ex: 123 TN 4567" required />
                                </div>
                                <div className="form-group">
                                    <label>Marque *</label>
                                    <input name="marque" value={formData.marque} onChange={handleFormChange} placeholder="Ex: Peugeot, Ford..." required />
                                </div>
                                <div className="form-group">
                                    <label>Type de véhicule *</label>
                                    <select name="type" value={formData.type} onChange={handleFormChange} required>
                                        <option value="">Sélectionner...</option>
                                        <option value="Utilitaire">Utilitaire</option>
                                        <option value="Camion">Camion</option>
                                        <option value="Voiture">Voiture</option>
                                        <option value="Pick-up">Pick-up</option>
                                        <option value="Fourgon">Fourgon</option>
                                        <option value="Autre">Autre</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Dates d'expiration (Alertes)</h3>
                                <div className="form-group">
                                    <label>Assurance</label>
                                    <input type="date" name="assurance" value={formData.assurance} onChange={handleFormChange} />
                                </div>
                                <div className="form-group">
                                    <label>Visite Technique</label>
                                    <input type="date" name="visiteTechnique" value={formData.visiteTechnique} onChange={handleFormChange} />
                                </div>
                                <div className="form-group">
                                    <label>Vignette</label>
                                    <input type="date" name="vignette" value={formData.vignette} onChange={handleFormChange} />
                                </div>
                            </div>

                            <div className="form-footer">
                                <button type="button" className="btn-cancel" onClick={() => {
                                    setView('list');
                                    setIsEditing(false);
                                    setSelectedVehicule(null);
                                }}>Annuler</button>
                                <button type="submit" className="btn-submit" disabled={loading}>
                                    {loading ? 'Enregistrement...' : (isEditing ? 'Enregistrer les modifications' : 'Enregistrer le véhicule')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {view === 'details' && (
                <div className="veh-details-view">
                    <button className="btn-back" onClick={() => setView('list')}>← Retour au dashboard</button>
                    
                    <div className="veh-details-header">
                        <h2>Historique des dépenses : {selectedVehicule.matricule}</h2>
                        <div className="veh-summary-mini">
                            <span>{selectedVehicule.marque}</span> | <span>{selectedVehicule.type}</span>
                        </div>
                    </div>

                    <div className="veh-expenses-table-wrap">
                        <h3>Historique des dépenses</h3>
                        <table className="veh-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Catégorie</th>
                                    <th>Montant</th>
                                    <th>Mode</th>
                                    <th>Responsable</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map(e => (
                                    <tr key={e._id}>
                                        <td>{new Date(e.datePaiement).toLocaleDateString()}</td>
                                        <td><span className="expense-type-pill">{e.type}</span></td>
                                        <td>{e.categorie}</td>
                                        <td className="amount">{e.montant.toLocaleString()} TND</td>
                                        <td style={{ textTransform: 'capitalize' }}>{e.modePaiement || '—'}</td>
                                        <td>{e.responsable?.nom} {e.responsable?.prenom}</td>
                                        <td className="notes">{e.notes}</td>
                                    </tr>
                                ))}
                                {expenses.length === 0 && <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>Aucune dépense enregistrée.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    <div className="veh-notes-section">
                        <h3>Notes & Historique</h3>
                        <div className="veh-notes-list">
                            {(selectedVehicule.notes || []).map((note, idx) => (
                                <div key={idx} className="veh-note-item">
                                    <div className="veh-note-meta">
                                        <strong>{note.auteur?.nom} {note.auteur?.prenom}</strong>
                                        <span>{new Date(note.date).toLocaleString()}</span>
                                    </div>
                                    <div className="veh-note-content">{note.content}</div>
                                </div>
                            ))}
                            {(!selectedVehicule.notes || selectedVehicule.notes.length === 0) && <p>Aucune note pour ce véhicule.</p>}
                        </div>
                        <div className="veh-add-note">
                            <textarea 
                                placeholder="Ajouter une note..." 
                                value={newNote} 
                                onChange={(e) => setNewNote(e.target.value)}
                            />
                            <button className="btn-submit" onClick={() => handleAddNote(selectedVehicule._id)}>Ajouter la note</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .veh-mgmt-container { padding: 30px; background: #f8fafc; min-height: 100vh; }
                .veh-header-actions { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    margin-bottom: 30px; 
                }
                .veh-header-actions h1 { font-size: 1.8rem; color: #1e293b; font-weight: 800; }
                
                .btn-add-veh {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 10px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
                    transition: all 0.3s ease;
                }
                .btn-add-veh:hover { transform: translateY(-2px); box-shadow: 0 6px 15px rgba(16, 185, 129, 0.3); }

                .veh-dashboard-grid { 
                    display: grid; 
                    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); 
                    gap: 25px; 
                }

                .veh-card { 
                    background: #fff; 
                    border-radius: 16px; 
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05); 
                    padding: 24px; 
                    transition: all 0.3s ease;
                    border: 1px solid #f1f5f9;
                }
                .veh-card:hover { transform: translateY(-5px); box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
                .veh-card-header { border-bottom: 1px solid #f1f5f9; padding-bottom: 18px; margin-bottom: 18px; }
                .veh-plate { font-size: 1.5rem; font-weight: 900; color: #0f172a; letter-spacing: 2px; }
                .veh-brand { color: #64748b; font-size: 0.95rem; margin-top: 6px; font-weight: 500; }

                .veh-docs-status { display: flex; flex-direction: column; gap: 12px; }
                .veh-doc-row { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    padding: 10px 14px; 
                    background: #f8fafc; 
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: 1px solid transparent;
                }
                .veh-doc-row:hover { background: #f1f5f9; border-color: #e2e8f0; }
                .veh-doc-row span:first-child { font-weight: 600; color: #475569; font-size: 0.9rem; }

                .veh-status-badge { 
                    color: white; 
                    padding: 4px 12px; 
                    border-radius: 20px; 
                    font-size: 0.75rem; 
                    font-weight: 700; 
                }

                .veh-card-footer { 
                    margin-top: 24px; 
                    padding-top: 20px; 
                    border-top: 1px solid #f1f5f9; 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                }
                .veh-total-cost .label { font-size: 0.8rem; color: #94a3b8; display: block; margin-bottom: 2px; }
                .veh-total-cost .value { font-weight: 800; color: #0f172a; font-size: 1.1rem; }
                
                .veh-actions { display: flex; gap: 12px; }
                .btn-icon { 
                    background: #f1f5f9; 
                    border: none; 
                    border-radius: 10px; 
                    width: 40px; 
                    height: 40px; 
                    cursor: pointer; 
                    font-size: 1.2rem;
                    display: flex; align-items: center; justify-content: center;
                    transition: all 0.2s;
                }
                .btn-icon:hover { background: #e2e8f0; transform: scale(1.05); }
                .btn-delete:hover { background: #fee2e2; color: #ef4444; }

                /* Form Styles */
                .veh-form-card {
                    background: #fff;
                    border-radius: 20px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
                    max-width: 900px;
                    margin: 0 auto;
                    overflow: hidden;
                }
                .veh-form-header {
                    background: #0f172a;
                    padding: 30px 40px;
                    color: white;
                }
                .veh-form-header h2 { margin: 0; font-size: 1.6rem; }
                .veh-form-header p { margin: 8px 0 0; color: #94a3b8; font-size: 0.95rem; }

                .veh-form-grid {
                    padding: 40px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 40px;
                }
                .form-section { display: flex; flex-direction: column; gap: 20px; }
                .form-section h3 { 
                    font-size: 1.1rem; 
                    color: #0f172a; 
                    margin: 0 0 10px; 
                    padding-bottom: 10px; 
                    border-bottom: 2px solid #f1f5f9; 
                }
                .form-group { display: flex; flex-direction: column; gap: 8px; }
                .form-group label { font-size: 0.85rem; font-weight: 700; color: #475569; }
                .form-group input {
                    padding: 12px 16px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 0.95rem;
                    transition: all 0.2s;
                }
                .form-group input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

                .form-footer {
                    grid-column: span 2;
                    display: flex;
                    justify-content: flex-end;
                    gap: 15px;
                    margin-top: 20px;
                    padding-top: 30px;
                    border-top: 1px solid #f1f5f9;
                }
                .btn-cancel { background: #f1f5f9; color: #475569; border: none; padding: 12px 25px; border-radius: 10px; font-weight: 600; cursor: pointer; }
                .btn-submit { background: #3b82f6; color: white; border: none; padding: 12px 30px; border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.3s; }
                .btn-submit:hover { background: #2563eb; transform: translateY(-2px); }
                .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

                .btn-back { 
                    margin-bottom: 25px; 
                    padding: 10px 20px; 
                    border: none; 
                    background: #fff; 
                    color: #0f172a; 
                    font-weight: 700;
                    border-radius: 10px; 
                    cursor: pointer; 
                    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
                    transition: all 0.2s;
                }
                .btn-back:hover { background: #f1f5f9; }

                .veh-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                .veh-table th { background: #f9fafb; padding: 15px; text-align: left; color: #374151; font-weight: 600; }
                .veh-table td { padding: 15px; border-top: 1px solid #f3f4f6; color: #4b5563; }
                .expense-type-pill { background: #eff6ff; color: #2563eb; padding: 4px 10px; border-radius: 6px; font-size: 0.85rem; font-weight: 600; }
                .amount { font-weight: 700; color: #111827; }
                .notes { font-size: 0.9rem; color: #6b7280; }

                .veh-notes-section { margin-top: 40px; background: #fff; padding: 25px; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                .veh-notes-section h3 { margin-bottom: 20px; color: #1e293b; }
                .veh-notes-list { display: flex; flex-direction: column; gap: 15px; margin-bottom: 25px; max-height: 400px; overflow-y: auto; }
                .veh-note-item { padding: 15px; background: #f8fafc; border-radius: 10px; border-left: 4px solid #3b82f6; }
                .veh-note-meta { display: flex; justify-content: space-between; font-size: 0.85rem; margin-bottom: 5px; color: #64748b; }
                .veh-note-content { color: #334155; line-height: 1.5; }
                
                .veh-add-note { display: flex; flex-direction: column; gap: 10px; }
                .veh-add-note textarea { padding: 12px; border-radius: 10px; border: 1px solid #e2e8f0; min-height: 100px; resize: vertical; }
                .veh-expenses-table-wrap h3 { margin-bottom: 15px; color: #1e293b; }
            `}</style>
        </div>
    );
};

export default VehiculeManagement;
